import {
  Box,
  mdBumps,
  ModalHeader,
  SectionProps,
  Typo,
  useModal,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router'

import {
  CoverOption,
  Data,
  getPolicyVersion,
  PolicyAdjustmentResponse,
} from '../../../domain'
import { usePolicyAdjustment, usePolicyFormData } from '../../../hooks/adjustments/policy'
import { useCoverLevelOption } from '../../../hooks/coverOption'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { FormConfig } from '../../../state/configuration'
import { usePrincipal } from '../../authentication'
import { FormDisabled } from '../../formBuilder'
import { AdjustmentModal } from '../../product/policy/adjustments/admin'
import { AddOnPath } from '../../product/policy/covers/AddOnPath'
import { PolicyPath } from '../../product/policy/PolicyPath'
import { usePolicy } from '../../product/policy/PolicyProvider'
import { useProduct } from '../../product/ProductProvider'
import { CoverSection } from '../covers'

export const PolicyCoverSection: FunctionComponent<SectionProps> = (props) => {
  const { policy } = usePolicy()
  const { product } = useProduct()

  const { quote } = getPolicyVersion(policy)

  const { coverLevelConfig } = useCoverLevelOption(quote, product)

  const { isAdmin } = usePrincipal()

  const modal = useModal()

  const onChange = useCallback(
    ({ coverOption }: CoverOption) => {
      const { form } =
        coverLevelConfig?.covers?.optional?.find(({ code }) => code === coverOption) ?? {}

      if (!form) {
        throw new Error(`No form found for optional cover ${coverOption}`)
      }

      modal.show({
        content: <PolicyEditOptionalCoverModal form={form} onComplete={modal.hide} />,
      })
    },
    [coverLevelConfig, modal],
  )

  return (
    <CoverSection quote={quote} onChange={isAdmin ? onChange : undefined} {...props} />
  )
}

const tKey = TKey('components.edit-optional-cover-modal')

interface EditOptionalCoverModalProps {
  form: FormConfig
  hidden?: FormConfig
  onComplete: () => void
}

const PolicyEditOptionalCoverModal: FunctionComponent<EditOptionalCoverModalProps> = ({
  form,
  hidden,
  onComplete,
}) => {
  const { t } = useWriskTranslation()
  const navigate = useNavigate()
  const modal = useModal()

  const { policy } = usePolicy()
  const { isAdmin } = usePrincipal()

  const data = usePolicyFormData(form, hidden)

  const onAdjustmentSubmitted = useCallback(
    async (response: PolicyAdjustmentResponse | undefined) => {
      if (response) {
        navigate(
          generatePath([PolicyPath.ADD_ONS, AddOnPath.REVIEW].join('/'), {
            adjustmentId: response.adjustmentId,
          }),
        )
      }

      onComplete()
    },
    [navigate, onComplete],
  )

  const onSubmitAdjustment = usePolicyAdjustment({
    form,
    onSubmitted: onAdjustmentSubmitted,
  })

  const onSubmit = useCallback(
    (updatedData: Data) => {
      if (isAdmin) {
        modal.show({
          content: (
            <AdjustmentModal
              updatedData={updatedData}
              policy={policy}
              onCancel={modal.hide}
              onSubmitAdjustment={onSubmitAdjustment}
            />
          ),
        })
      } else {
        void onSubmitAdjustment.execute(updatedData)
      }
    },
    [isAdmin, modal, onSubmitAdjustment, policy],
  )

  return (
    <Box>
      <ModalHeader header={t(tKey('header'))} />
      <Box marginBottom={mdBumps}>
        <Typo>{t(tKey('description'))}</Typo>
      </Box>
      <FormDisabled
        id='editOptionalCover'
        data={data}
        config={form}
        onSubmit={onSubmit}
        loading={onSubmitAdjustment.loading}
        tKey={TExtends(tKey, 'inputs')}
        t={t}
      />
    </Box>
  )
}
